import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use';
import { Helmet } from 'react-helmet-async';
import { CYLINDO_ACCOUNT_ID } from 'commons/constants';
import usePdpId from '../state/context/usePdpId';

const SCRIPT_ID = 'cylindoScript';

const loadCylindo = callback => {
  const existingScript = document.getElementById(SCRIPT_ID);

  if (!existingScript) {
    const script = document.createElement('script');
    script.src = 'https://viewer-cdn.cylindo.com/v1/index.mjs';
    script.id = SCRIPT_ID;
    script.type = 'module';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (callback) callback();
    };
  } else if (callback) {
    callback();
  }
};

const PDPCylindo3dViewer = ({
  containerId = 'PDPCylindo3dViewer',
  cylindoFeatures,
  isMobile,
  rotateIndex,
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const controls = isMobile ? 'ar' : 'ar zoom';
  const productId = usePdpId();

  useDebounce(
    () => {
      if (productId && Object.keys(cylindoFeatures).length > 0) {
        loadCylindo(() => {
          // make sure the cylindo framework has been "installed"
          setIsReady(true);
        });
      }
    },
    100,
    [productId, cylindoFeatures]
  );

  useEffect(() => {
    if (!isMobile && containerRef.current && rotateIndex >= 0 && isReady) {
      const viewer = document.querySelector(`cylindo-viewer#${containerId}`);

      if (viewer && typeof viewer.animateToFrame === 'function') {
        if (rotateIndex === 0) {
          viewer.animateToFrame(1);
        } else if (rotateIndex === 1) {
          viewer.animateToFrame(4);
        } else if (rotateIndex === 2) {
          viewer.animateToFrame(9);
        } else if (rotateIndex === 3) {
          viewer.animateToFrame(14);
        } else if (rotateIndex === 4) {
          viewer.animateToFrame(17);
        }
      }
    }
  }, [containerRef, isMobile, rotateIndex, isReady, containerId]);

  useLayoutEffect(() => {
    if (
      containerRef.current &&
      Object.keys(cylindoFeatures).length &&
      isReady
    ) {
      containerRef.current.features = cylindoFeatures;
    }
  }, [containerRef, cylindoFeatures, isReady]);

  // feature query parameter for fallback image.
  const featuresParam = useMemo(() => {
    if (!Object.keys(cylindoFeatures).length) {
      return null;
    }

    return Object.entries(cylindoFeatures)
      .reduce((acc, [key, value]) => {
        // Replace spaces with underscores
        const cleanValue = value.replace(/ /g, '%20');
        return [...acc, `feature=${key}:${cleanValue}`];
      }, [])
      .join('&');
  }, [cylindoFeatures]);

  useLayoutEffect(() => {
    const viewer = document.querySelector('cylindo-viewer');

    viewer?.addEventListener('loaded', () => {
      const { modelViewer } = viewer;
      if (modelViewer) modelViewer.exposure = 0.9;
    });
  }, []);
  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://content.cylindo.com" />
      </Helmet>
      <div className="w-full !h-full ml-auto mr-auto max-md:min-h-[375px] flex justify-center items-center [&_img]:w-4/5 lg:[&_cylindo-viewer]:aspect-[1.8]">
        <cylindo-viewer
          controls={controls}
          ref={containerRef}
          key={containerId}
          customer-id={CYLINDO_ACCOUNT_ID}
          code={productId?.toString()}
          id={containerId}
        >
          {/* A fallback image when cylindo image is not available */}
          <img
            slot="placeholder"
            alt="placeholder"
            src={`https://content.cylindo.com/api/v2/${CYLINDO_ACCOUNT_ID}/products/${productId?.toString()}/frames/1/${productId?.toString()}.webp?encoding=webp-100&size=1024&${featuresParam}`}
          />
        </cylindo-viewer>
      </div>
    </>
  );
};

PDPCylindo3dViewer.propTypes = {
  containerId: PropTypes.string,
  cylindoFeatures: PropTypes.arrayOf(PropTypes.string),
  isMobile: PropTypes.bool,
  rotateIndex: PropTypes.number,
};

export default PDPCylindo3dViewer;
